import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/ToastOverrides.css';
import axios from 'axios';
import { getApiUrl } from './config/api';
import { applyStoredLanguage } from './utils/languagePreference';
import { migrateLocalWishlistToServer, invalidateWishlistCache, invalidateMeetFavCache } from './utils/wishlistAPI';
import RootPage from './pages/RootPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import AtozasSpaCallback from './pages/AtozasSpaCallback';
import SearchResultsPage from './pages/SearchResultsPage';
import PropertyDetailsPage from './pages/PropertyDetailsPage';
import PropertiesPage from './pages/PropertiesPage';
import HostPage from './pages/HostPage';
import HostPropertyDetailsPage from './pages/HostPropertyDetailsPage';
import AddStayPropertyPage from './pages/AddStayPropertyPage';
import HostMessagesPage from './pages/HostMessagesPage';
import WishlistPage from './pages/WishlistPage';
import RefundStatusPage from './pages/RefundStatusPage';
import RoleBasedRoute from './components/RoleBasedRoute';
// Meeting pages
import MeetingRooms from './pages/meetings/MeetingRooms';
import BookRoomFlow from './pages/meetings/BookRoomFlow';
import MyBookings from './pages/meetings/MyBookings';
import RoomAvailabilitySchedulePage from './pages/meetings/RoomAvailabilitySchedulePage';
import MeetingHome from './pages/meetings/MeetingHome';
import Favourite from './pages/meetings/Favourite';
import Profile from './pages/meetings/Profile';
import CreateTeam from './pages/meetings/CreateTeam';
import StatusPage from './pages/meetings/StatusPage';
import PrivateWorkspaceDashboard from './pages/meetings/PrivateWorkspaceDashboard';
import MeetingSearchResults from './pages/meetings/MeetingSearchResults';
import MeetingRoomDetailsPage from './pages/meetings/MeetingRoomDetailsPage';
import AddYourSpace from './pages/meetings/AddYourSpace';
import ModernBookingPage from './pages/meetings/ModernBookingPage';
import BookRoom from './pages/meetings/BookRoom';
import BookMeetingRoomPage from './pages/BookMeetingRoomPage';
import BookingPage from './pages/BookingPage';
import BookingPageMeeting from './pages/meetings/BookingPage';
import PaymentPage from './pages/PaymentPage';
import UserPaymentHistory from './pages/UserPaymentHistory';
import MeetingPaymentPage from './pages/meetings/MeetingPaymentPage';
import BookingConfirmationPage from './pages/BookingConfirmationPage';
import TripsPage from './pages/TripsPage';
import BookingDetailsPage from './pages/BookingDetailsPage';
import NotificationsPage from './pages/NotificationsPage';
import AccountPage from './pages/AccountPage';
import HelpPage from './pages/HelpPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import SitemapPage from './pages/SitemapPage';
import FeedbackPage from './pages/FeedbackPage';
import OffersDealsPage from './pages/OffersDealsPage';
import InstallAppPrompt from './components/InstallAppPrompt';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import MeetingBottomNav from './components/MeetingBottomNav';
import BackButton from './components/BackButton';
import { registerPushNotifications } from './services/pushNotificationService';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminCompanies from './pages/admin/AdminCompanies';
import AdminProperties from './pages/admin/AdminProperties';
import AdminBookings from './pages/admin/AdminBookings';
import AdminPayments from './pages/admin/AdminPayments';
import AdminReviews from './pages/admin/AdminReviews';
import AdminReports from './pages/admin/AdminReports';
import AdminLogs from './pages/admin/AdminLogs';
import WorkspaceRoute from './components/WorkspaceRoute';
import WorkspacePermissionRoute from './components/WorkspacePermissionRoute';
import WorkspaceLayout from './pages/workspace/WorkspaceLayout';
import WorkspaceDashboard from './pages/workspace/WorkspaceDashboard';
import WorkspaceStaff from './pages/workspace/WorkspaceStaff';
import WorkspaceProperties from './pages/workspace/WorkspaceProperties';
import WorkspaceBookings from './pages/workspace/WorkspaceBookings';
import WorkspaceAnalytics from './pages/workspace/WorkspaceAnalytics';
import WorkspaceActivity from './pages/workspace/WorkspaceActivity';
import WorkspaceNotifications from './pages/workspace/WorkspaceNotifications';
import WorkspaceJoinPage from './pages/workspace/WorkspaceJoinPage';
import WorkspaceLoginPage from './pages/workspace/WorkspaceLoginPage';
import WorkspaceInvitePage from './pages/workspace/WorkspaceInvitePage';
import BookingAssistantPage from './pages/BookingAssistantPage';
import TripPlanPage from './pages/TripPlanPage';
import TripPlanGeneratePage from './pages/TripPlanGeneratePage';
import { AiAssistantProvider } from './context/AiAssistantContext';
import BookingAssistantFAB from './components/BookingAssistantFAB';
import AiAssistantChatWidget from './components/AiAssistantChatWidget';
import {
  handleWorkspaceSessionApiError,
  startWorkspaceSessionWatcher,
  syncWorkspaceAccessFromResponse,
} from './utils/workspaceSession';

function AppRuntimeEffects() {
  const location = useLocation();

  useEffect(() => {
    const retryRegistration = () => {
      registerPushNotifications().catch(() => {});
    };

    retryRegistration();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        retryRegistration();
      }
    };

    window.addEventListener('focus', retryRegistration);
    window.addEventListener('codex-authenticated', retryRegistration);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const handlePushEnabled = () => {
      toast.success('Browser notifications enabled', { toastId: 'push-enabled' });
    };
    window.addEventListener('push-notifications-enabled', handlePushEnabled);

    return () => {
      window.removeEventListener('focus', retryRegistration);
      window.removeEventListener('codex-authenticated', retryRegistration);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('push-notifications-enabled', handlePushEnabled);
    };
  }, [location.pathname]);

  return null;
}

function App() {
  useEffect(() => {
    applyStoredLanguage();
    startWorkspaceSessionWatcher();
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const validateStoredSession = async () => {
      const token = localStorage.getItem('token');

      // NOTE: No automatic ATOZAS login on app load — neither a silent redirect to
      // the IdP nor an /atozas/me session restore. ATOZAS login happens ONLY when the
      // user explicitly clicks "Continue with ATOZAS". After login the app stays
      // signed in via the stored JWT, and logout keeps the user signed out.
      if (!token) return;

      try {
        const response = await axios.get(getApiUrl('/api/auth/me'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!isCancelled) {
          syncWorkspaceAccessFromResponse(response.data);
        }
      } catch (error) {
        if (isCancelled) return;
        if (handleWorkspaceSessionApiError(error)) return;

        const status = error?.response?.status;
        const code = error?.response?.data?.code;

        if (status === 401 || status === 403 || code === 'AUTH_USER_NOT_FOUND') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('authRedirectPath');
        }
      }
    };

    validateStoredSession().then(() => {
      // Migrate any localStorage wishlist items to server after session is confirmed
      migrateLocalWishlistToServer().catch(() => {});
      invalidateWishlistCache();
      invalidateMeetFavCache();
    });

    return () => {
      isCancelled = true;
    };
  }, []);

  return (
    <Router>
      <AiAssistantProvider>
      <AppRuntimeEffects />
      <BackButton />
      <InstallAppPrompt />
      <PushNotificationPrompt />
      <Routes>
        <Route path="/" element={<RootPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/ai-assistant" element={<BookingAssistantPage />} />
        <Route path="/trip-plan/generate" element={<TripPlanGeneratePage />} />
        <Route path="/trip-plan/shared/:shareToken" element={<TripPlanPage />} />
        <Route path="/trip-plan/:planId" element={<TripPlanPage />} />
        <Route path="/join-workspace" element={<WorkspaceJoinPage />} />
        <Route path="/workspace/login" element={<WorkspaceLoginPage />} />
        <Route path="/workspace/invite" element={<WorkspaceInvitePage />} />
        <Route path="/workspace/invite/*" element={<WorkspaceInvitePage />} />
        <Route path="/auth/atozas/callback" element={<AtozasSpaCallback />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route 
          path="/search" 
          element={<SearchResultsPage />} 
        />
        <Route 
          path="/property/:propertyId" 
          element={<PropertyDetailsPage />} 
        />
        <Route 
          path="/properties" 
          element={<PropertiesPage />} 
        />
        <Route 
          path="/booking" 
          element={<BookingPage />} 
        />
        <Route 
          path="/payment" 
          element={<PaymentPage />} 
        />
        <Route
          path="/my-payments"
          element={<UserPaymentHistory />}
        />
        <Route
          path="/meetings/payment/:bookingId"
          element={<MeetingPaymentPage />}
        />
        <Route
          path="/meetings/payment"
          element={<MeetingPaymentPage />}
        />
        <Route 
          path="/booking-confirmation" 
          element={<BookingConfirmationPage />} 
        />
        <Route 
          path="/trips" 
          element={<TripsPage />} 
        />
        <Route 
          path="/booking-details/:bookingId" 
          element={<BookingDetailsPage />} 
        />
        <Route 
          path="/notifications" 
          element={<NotificationsPage />} 
        />
        <Route 
          path="/account" 
          element={<AccountPage />} 
        />
        <Route 
          path="/help" 
          element={<HelpPage />} 
        />
        <Route 
          path="/faq" 
          element={<HelpPage />} 
        />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route 
          path="/feedback" 
          element={<FeedbackPage />} 
        />
        <Route 
          path="/stays" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <SearchResultsPage />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/deals" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <SearchResultsPage />
            </RoleBasedRoute>
          } 
        />
        <Route path="/offers-deals" element={<OffersDealsPage />} />
        <Route 
          path="/experiences" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <SearchResultsPage />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/host" 
          element={<HostPage />} 
        />
        <Route 
          path="/host/add-property" 
          element={<AddStayPropertyPage />} 
        />
        <Route 
          path="/host/property/:propertyId" 
          element={<HostPropertyDetailsPage />} 
        />
        <Route 
          path="/host/messages" 
          element={<HostMessagesPage />} 
        />
        <Route
          path="/workspace"
          element={
            <WorkspaceRoute>
              <WorkspaceLayout />
            </WorkspaceRoute>
          }
        >
          <Route index element={<WorkspaceDashboard />} />
          <Route path="staff" element={<WorkspacePermissionRoute permission="manage_staff"><WorkspaceStaff /></WorkspacePermissionRoute>} />
          <Route path="properties" element={<WorkspacePermissionRoute permission="view_properties"><WorkspaceProperties /></WorkspacePermissionRoute>} />
          <Route path="bookings" element={<WorkspacePermissionRoute permission="manage_bookings"><WorkspaceBookings /></WorkspacePermissionRoute>} />
          <Route path="analytics" element={<WorkspacePermissionRoute permission="view_analytics"><WorkspaceAnalytics /></WorkspacePermissionRoute>} />
          <Route path="activity" element={<WorkspacePermissionRoute permission="view_activity"><WorkspaceActivity /></WorkspacePermissionRoute>} />
          <Route path="notifications" element={<WorkspacePermissionRoute permission="view_notifications"><WorkspaceNotifications /></WorkspacePermissionRoute>} />
        </Route>
        <Route 
          path="/wishlist" 
          element={<WishlistPage />} 
        />
        <Route path="/refund-status" element={<RefundStatusPage />} />
        {/* Meeting Routes */}
        <Route 
          path="/meetings/rooms" 
          element={<MeetingRooms />} 
        />
        <Route 
          path="/meetings/room/:roomId" 
          element={<MeetingRoomDetailsPage />} 
        />
        <Route 
          path="/meetings/book" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <BookRoom />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/meetings/book-flow" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <BookRoomFlow />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/meetings/modern-booking/:roomId" 
          element={<ModernBookingPage />} 
        />
        <Route 
          path="/meetings/booking/:roomId" 
          element={<BookingPageMeeting />} 
        />
        <Route 
          path="/meetings/my-bookings" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <MyBookings />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/meetings/schedule" 
          element={<RoomAvailabilitySchedulePage />} 
        />
        {/* Direct Meeting Booking Route - No role restrictions */}
        <Route 
          path="/meeting-book" 
          element={<RoomAvailabilitySchedulePage />} 
        />
        {/* Meeting Home Route - Landing page for meeting booking */}
        <Route 
          path="/meeting-home" 
          element={<MeetingHome />} 
        />
        <Route 
          path="/meetings/favourite" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <Favourite />
            </RoleBasedRoute>
          } 
        />
        <Route 
          path="/meetings/profile" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <Profile />
            </RoleBasedRoute>
          } 
        />
        {/* Create Team Route */}
        <Route 
          path="/meetings/create-team" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <CreateTeam />
            </RoleBasedRoute>
          } 
        />
        {/* Private Workspace Dashboard */}
        <Route
          path="/meetings/workspace"
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <PrivateWorkspaceDashboard />
            </RoleBasedRoute>
          }
        />
        {/* Status Page Route */}
        <Route 
          path="/meetings/status" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <StatusPage />
            </RoleBasedRoute>
          } 
        />
        {/* Meeting Search Route - redirects to search-results */}
        <Route 
          path="/meetings/search" 
          element={<MeetingSearchResults />} 
        />
        {/* Meeting Search Results Route */}
        <Route 
          path="/meetings/search-results" 
          element={<MeetingSearchResults />} 
        />
        {/* Add Your Space Route */}
        <Route 
          path="/meetings/add-your-space" 
          element={
            <RoleBasedRoute requiredRole="guest" redirectTo="/host">
              <AddYourSpace />
            </RoleBasedRoute>
          } 
        />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="companies" element={<AdminCompanies />} />
          <Route path="properties" element={<AdminProperties />} />
          <Route path="bookings" element={<AdminBookings />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="logs" element={<AdminLogs />} />
        </Route>
      </Routes>
      <BookingAssistantFAB />
      <AiAssistantChatWidget />
      <MeetingBottomNav />
      {/* Stable wrapper so React and react-toastify don't conflict on unmount (avoids removeChild on Node) */}
      <div key="toast-root">
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
      </AiAssistantProvider>
    </Router>
  );
}

export default App;
